// components/ContactForm.js
import React from 'react';
import {
  Box,
  Container,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  Button,
  VStack,
  Text,
  useToast,
  Alert,
  AlertIcon,
  Link,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useForm } from '../../hooks/useForm';
import { useAnalytics } from '../../hooks/useAnalytics';

const MotionBox = motion(Box);

const ContactForm = ({ data }) => {
  const initialFormData = {
    name: '',
    email: '',
    phone: '',
    interest: '',
    message: '',
  };

  const toast = useToast();
  
  const {
    formData,
    handleChange,
    handleSubmit,
    isSubmitting,
    prefilledData
  } = useForm(initialFormData, 'contact');

  // Analytics hook
  const { trackEvent } = useAnalytics();

  const handleFormSubmit = async (e) => {
    try {
      e.preventDefault(); // prevent default form submission
      await handleSubmit(e);

      // GA4: Track form submit event
      trackEvent('form_submit', {
        page: 'contact',
        selected_interest: formData.interest,
        message_length: formData.message.length,
      });
      
      toast({
        title: 'Message sent!',
        description: data.responseTime,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // Optional: Clear form after successful submission
      // setFormData(initialFormData);
      
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to send message. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // GA4: Track WhatsApp click
  const handleWhatsappClick = () => {
    trackEvent('whatsapp_click', {
      page: 'contact',
      contact_method: 'whatsapp',
      phone: '+919900309000',
    });
  };

  return (
    <Container maxW="container.md">
      <MotionBox
        as="form"
        onSubmit={handleFormSubmit}
        bg="white"
        p={{ base: 6, md: 10 }}
        borderRadius="2xl"
        boxShadow="xl"
        initial={{ y: 30, opacity: 0 }}
        whileInView={{ y: 0, opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <VStack spacing={6} align="stretch">
          {/* Show notification if we prefilled data */}
          {prefilledData && (prefilledData.name || prefilledData.email) && (
            <Alert status="info" borderRadius="md" fontSize="sm" color="brand.600">
              <AlertIcon />
              We've prefilled your information from previous visits
            </Alert>
          )}

          <FormControl isRequired>
            <FormLabel color="brand.700" fontWeight="600">Name</FormLabel>
            <Input
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Your name"
              focusBorderColor="accent.green"
              size="lg"
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel color="brand.700" fontWeight="600">Email</FormLabel>
            <Input
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="your@email.com"
              focusBorderColor="accent.green"
              size="lg"
            />
          </FormControl>

          <FormControl>
            <FormLabel color="brand.700" fontWeight="600">Phone (Optional)</FormLabel>
            <Input
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              placeholder="+91-XXXXXXXXXX"
              focusBorderColor="accent.green"
              size="lg"
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel color="brand.700" fontWeight="600">I'm interested in</FormLabel>
            <Select
              name="interest"
              value={formData.interest}
              onChange={handleChange}
              placeholder="Select an option"
              focusBorderColor="accent.green"
              size="lg"
            >
              {data.interestOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl isRequired>
            <FormLabel color="brand.700" fontWeight="600">Message</FormLabel>
            <Textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Tell us about your needs..."
              focusBorderColor="accent.green"
              size="lg"
              rows={5}
            />
          </FormControl>

          {/* Send Message Button */}
          <Button
            type="submit"
            size="lg"
            bg="accent.green"
            color="white"
            _hover={{ bg: 'accent.gold', color: 'brand.800' }}
            width="full"
            py={7}
            isLoading={isSubmitting}
            loadingText="Sending..."
          >
            Send Message
          </Button>

          {/* WhatsApp Quick Chat Link */}
          <Link
            href="https://wa.me/919900309000"
            onClick={handleWhatsappClick}
            color="brand.700"
            fontWeight="600"
            textAlign="center"
            display="block"
          >
            Quick Chat
          </Link>

          <Text fontSize="sm" color="brand.400" textAlign="center">
            {data.responseTime}
          </Text>
        </VStack>
      </MotionBox>
    </Container>
  );
};

export default ContactForm;
